import { Logic } from '~common/ui-logic/logic'
import { executeTask } from '~common/ui-logic/tasks'
import type { EmailPreviewProps, EmailPreviewState } from './types'
import { Email } from '~common/storage/email'
import { parseFullMessage } from '~common/google-api/utils'
import { GmailFullMessageResponse } from '~common/google-api/types'
import { TaskState } from '~/services/cache/types'

export class EmailPreviewLogic extends Logic<
    EmailPreviewProps,
    EmailPreviewState
> {
    getInitialState(): EmailPreviewState {
        return {
            loadState: 'pristine',
            emailData: null,
            invoiceDocumentId: null,
            emailLoadState: 'pristine',
            pdfLoadState: 'pristine',
            pdfBlob: null,
            error: null,
            downloadingAttachmentId: null,
            documentToShow: undefined,
            matchLoadingState: 'pristine',
        }
    }

    async initialize(): Promise<void> {
        await executeTask(this, 'loadState', async () => {
            await this.loadEmailCoreData()
        })
    }

    shouldReload(
        oldDeps: EmailPreviewProps,
        newDeps: EmailPreviewProps,
    ): boolean {
        return (
            oldDeps.emailId !== newDeps.emailId ||
            oldDeps.invoiceId !== newDeps.invoiceId
        )
    }

    private async loadEmailCoreData(): Promise<void> {
        let emailData: Email | null = null
        let invoiceId: string | null = null
        let externalId: string | undefined = undefined
        try {
            if (this.deps.invoiceId) {
                this.loadInvoiceDocumentFromGDrive(this.deps.invoiceId)
                this.setState({ documentToShow: 'invoice' })
                try {
                    invoiceId = this.deps.invoiceId
                    const invoice =
                        this.deps.services.cache.getKey('invoices')[
                            this.deps.invoiceId
                        ]

                    if (invoice.sourceEmailId) {
                        try {
                            // Parallelize email fetching and GDrive document loading
                            const emailDataResults =
                                await this.deps.storageModules.emailInvoice.getEmailsByIds(
                                    [invoice.sourceEmailId],
                                )
                            emailData = emailDataResults[invoice.sourceEmailId]
                        } catch (error) {
                            console.error('Error loading email data:', error)
                            this.setState({
                                error:
                                    error instanceof Error
                                        ? error.message
                                        : 'Failed to load email2',
                            })
                        }
                    }
                } catch (error) {
                    console.error('Error loading email data:', error)
                    this.setState({
                        error:
                            error instanceof Error
                                ? error.message
                                : 'Failed to load email1',
                    })
                }
            } else if (this.deps.emailId) {
                let emailId = this.deps.emailId

                this.setState({ documentToShow: 'email' })
                if (emailId.startsWith('temp-')) {
                    emailData =
                        this.deps.services.cache.getKey('emails')[emailId]
                    externalId = emailData?.externalId
                } else {
                    const emailDataResults =
                        await this.deps.storageModules.emailInvoice.getEmailsByIds(
                            [this.deps.emailId],
                        )

                    emailData = emailDataResults[this.deps.emailId]
                    // Load document IDs first (blocking)
                    if (!this.deps.invoiceId && emailData != null) {
                        const emailDocumentResults =
                            await this.deps.storageModules.emailInvoice.getDocumentIdsByEmailIds(
                                [emailData.id],
                            )
                        const documentresult =
                            emailDocumentResults[emailData.id]
                        invoiceId = documentresult?.[0] ?? null
                    }
                    if (invoiceId) {
                        this.loadInvoiceDocumentFromGDrive(invoiceId)
                    }
                    externalId = emailData?.externalId
                }
                // Start HTML fetching in background (non-blocking)
            }

            if (externalId) {
                this.loadEmailHTMLAsync(externalId, emailData?.accountEmail)
            }

            this.setState({
                emailData: emailData,
                invoiceDocumentId: invoiceId,
                error: null,
            })
        } catch (error) {
            console.error('Error loading email data:', error)
            this.setState({
                error:
                    error instanceof Error
                        ? error.message
                        : 'Failed to load email3',
            })
        }
    }

    async fetchEmailHTML(
        emailExternalId: string,
        accountEmail?: string,
    ): Promise<string | null> {
        const emailData = await this.deps.services.googleApi.getGmailMessage({
            messageId: emailExternalId,
            accountEmail,
        })
        const htmlContent = parseFullMessage(
            emailData as GmailFullMessageResponse,
        ).htmlContent
        // No persistence; only return for preview
        return htmlContent
    }

    async loadEmailHTMLAsync(
        emailExternalId?: string,
        accountEmail?: string,
    ): Promise<void> {
        await executeTask(this, 'emailLoadState', async () => {
            if (!emailExternalId) {
                return
            }
            const htmlContent = await this.fetchEmailHTML(
                emailExternalId,
                accountEmail,
            )
            // Update state with fetched HTML content
            this.setState({
                emailData: this.state.emailData
                    ? {
                          ...this.state.emailData,
                          htmlContent: htmlContent ?? undefined,
                      }
                    : null,
            })
        }).catch((error) => {
            console.error('Failed to load email HTML asynchronously:', error)
            // Don't set error state for background HTML loading failures
            // The UI will still show without HTML content
        })
    }

    async loadInvoiceDocumentFromGDrive(invoiceId: string): Promise<void> {
        await executeTask(this, 'pdfLoadState', async () => {
            if (this.state.pdfBlob != null) {
                return
            }
            try {
                const invoice =
                    this.deps.services.cache.getKey('invoices')[invoiceId]
                if (invoice.googleDriveFileId) {
                    const googleDriveFile =
                        await this.deps.services.googleApi.downloadGoogleDriveFile(
                            invoice.googleDriveFileId,
                        )

                    if (googleDriveFile.data) {
                        this.setState({
                            pdfBlob: googleDriveFile.data,
                            error: null,
                        })
                    } else {
                        throw new Error(
                            'No PDF data received from Google Drive',
                        )
                    }
                } else {
                    throw new Error('No file path found for invoice')
                }
            } catch (error) {
                console.error('Error loading PDF from Google Drive:', error)
                this.setState({
                    error:
                        error instanceof Error
                            ? `Failed to load PDF: ${error.message}`
                            : 'Failed to load PDF',
                    pdfBlob: null,
                })
            }
        })
    }

    async downloadInvoiceDocumentFromGDrive(invoiceId: string): Promise<void> {
        try {
            const invoice =
                this.deps.services.cache.getKey('invoices')[invoiceId]

            if (!invoice) {
                throw new Error('Invoice not found')
            }

            if (!invoice.googleDriveFileId) {
                throw new Error('No file ID found for invoice')
            }

            // Download the file from Google Drive
            const googleDriveFile =
                await this.deps.services.googleApi.downloadGoogleDriveFile(
                    invoice.googleDriveFileId,
                )

            if (!googleDriveFile.data) {
                throw new Error('No file data received from Google Drive')
            }

            // Create blob URL for download
            const blobUrl = URL.createObjectURL(googleDriveFile.data)

            // Create temporary anchor element to trigger download
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = googleDriveFile.filename || `invoice-${invoiceId}.pdf`
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            // Clean up blob URL after a delay to ensure download starts
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
        } catch (error) {
            console.error(
                'Error downloading invoice document from Google Drive:',
                error,
            )
            throw new Error(
                error instanceof Error
                    ? `Failed to download invoice: ${error.message}`
                    : 'Failed to download invoice',
            )
        }
    }

    async downloadAttachment(attachmentId: string): Promise<void> {
        if (!this.state.emailData) {
            throw new Error('No email loaded')
        }

        const accessToken = this.deps.services.googleApi.getAccessToken()
        if (!accessToken) {
            throw new Error('Not authenticated with Google')
        }

        // Find the attachment to get its filename and mimeType
        const attachment = this.state.emailData.originalAttachments.find(
            (att) => att.id === attachmentId,
        )

        if (!attachment) {
            throw new Error('Attachment not found')
        }

        // Set loading state
        this.setState({ downloadingAttachmentId: attachmentId })

        try {
            // Use the existing GoogleApiService method instead of direct fetch
            // This ensures proper authentication and avoids CORS issues
            const attachmentData =
                await this.deps.services.googleApi.downloadGmailAttachment({
                    messageId: this.state.emailData.externalId,
                    attachmentId: attachmentId,
                    accountEmail: this.state.emailData.accountEmail,
                })

            if (!attachmentData.data) {
                throw new Error('No attachment data received')
            }

            // Convert base64 to blob
            const binaryString = atob(attachmentData.data)
            const bytes = new Uint8Array(binaryString.length)
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i)
            }
            const blob = new Blob([bytes], { type: attachment.mimeType })

            // Create blob URL
            const blobUrl = URL.createObjectURL(blob)

            // Create temporary anchor element to trigger download
            const a = document.createElement('a')
            a.href = blobUrl
            a.download = attachment.filename
            a.style.display = 'none'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            // Clean up blob URL after a delay to ensure download starts
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)

            // Clear loading state
            this.setState({ downloadingAttachmentId: null })
        } catch (error) {
            console.error('Error downloading attachment:', error)

            // Clear loading state
            this.setState({ downloadingAttachmentId: null })

            throw new Error(
                error instanceof Error
                    ? `Failed to download attachment: ${error.message}`
                    : 'Failed to download attachment',
            )
        }
    }

    async downloadEmailAsPdf(fullHtml: string): Promise<void> {
        if (!this.state.emailData) {
            throw new Error('No email loaded')
        }

        const subject =
            this.state.emailData.subject
                ?.replace(/[^a-z0-9]/gi, '_')
                .toLowerCase() || 'email'
        const filename = `${subject}.pdf`

        try {
            // Pre-generate a Google Drive file ID
            const fileIds =
                await this.deps.services.googleApi.generateDriveFileIds(1)
            const preGeneratedFileId = fileIds[0]
            if (!preGeneratedFileId) {
                throw new Error('Failed to generate Drive file ID')
            }

            const result =
                await this.deps.services.cloudFunctions.processHTMLToPDF({
                    html: fullHtml,
                    fileName: filename,
                    invoiceDate: new Date(
                        this.state.emailData.receivedDate,
                    ).getTime(),
                    fileId: preGeneratedFileId,
                })

            if (result.success === true && result.pdfData) {
                // Convert base64 to blob
                const binary = atob(result.pdfData)
                const len = binary.length
                const bytes = new Uint8Array(len)
                for (let i = 0; i < len; i++) {
                    bytes[i] = binary.charCodeAt(i)
                }
                const blob = new Blob([bytes], { type: 'application/pdf' })

                const blobUrl = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = blobUrl
                a.download = filename
                a.style.display = 'none'
                document.body.appendChild(a)
                a.click()
                document.body.removeChild(a)
                setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
            } else {
                if (result.error) {
                    throw new Error(result.error || 'Failed to generate PDF')
                }
                throw new Error('Failed to generate PDF')
            }
        } catch (error) {
            console.error('Error generating or downloading PDF:', error)
            throw error instanceof Error
                ? error
                : new Error('Error generating or downloading PDF')
        }
    }

    async setDocumentView(view: 'email' | 'invoice'): Promise<void> {
        if (view === 'email') {
            const email = this.state.emailData
            if (
                email &&
                (email.htmlContent == null || email.htmlContent === '')
            ) {
                try {
                    const htmlContent = await this.fetchEmailHTML(
                        email.externalId,
                        email.accountEmail,
                    )
                    this.setState({
                        emailData: {
                            ...email,
                            htmlContent: htmlContent ?? undefined,
                        },
                        documentToShow: view,
                        emailLoadState: 'success',
                    })
                    return
                } catch (error) {
                    console.error('Failed to fetch email HTML on toggle', error)
                    this.setState({
                        documentToShow: view,
                        emailLoadState: 'error',
                    })
                    return
                }
            } else if (email && email.htmlContent) {
                // HTML already exists, ensure emailLoadState is success
                this.setState({
                    documentToShow: view,
                    emailLoadState: 'success',
                })
                return
            }
        }
        this.setState({ documentToShow: view })
    }

    analyzeMessage = async (email: Email): Promise<void> => {
        await executeTask(this, 'pdfLoadState', async () => {
            if (!email) {
                return
            }
            const message = email
            this.deps.services.cache.setEmail([
                {
                    ...message,
                    analysisStatus: 'running',
                },
            ])
            try {
                await this.deps.services.invoiceProcessor.processEmailForInvoices(
                    message,
                    undefined,
                    true,
                )
                // Update email in cache with success status
                this.deps.services.cache.setEmail([
                    {
                        ...message,
                        analysisStatus: 'full-success',
                    },
                ])

                // Get the invoice ID(s) that were created
                const emailInvoicePairs =
                    this.deps.services.cache.getKey('emailInvoices')
                const invoiceIds = emailInvoicePairs[message.id]

                // If no invoice IDs in cache, try fetching from storage
                let invoiceId: string | null = null
                if (invoiceIds && invoiceIds.length > 0) {
                    invoiceId = invoiceIds[0]
                } else {
                    const emailDocumentResults =
                        await this.deps.storageModules.emailInvoice.getDocumentIdsByEmailIds(
                            [message.id],
                        )
                    const documentResult = emailDocumentResults[message.id]
                    invoiceId = documentResult?.[0] ?? null
                }

                // Update state with invoice ID and load the PDF
                if (invoiceId) {
                    this.setState({
                        invoiceDocumentId: invoiceId,
                        emailData: this.state.emailData
                            ? {
                                  ...this.state.emailData,
                                  analysisStatus: 'full-success',
                              }
                            : null,
                    })

                    // Load the PDF (inline logic since we're already in pdfLoadState task)
                    try {
                        let invoice =
                            this.deps.services.cache.getKey('invoices')[
                                invoiceId
                            ]

                        // If invoice not in cache, fetch from storage
                        if (!invoice) {
                            const invoices =
                                await this.deps.storageModules.emailInvoice.getInvoicesByIds(
                                    [invoiceId],
                                )
                            invoice = invoices[invoiceId]
                        }

                        if (invoice?.googleDriveFileId) {
                            const googleDriveFile =
                                await this.deps.services.googleApi.downloadGoogleDriveFile(
                                    invoice.googleDriveFileId,
                                )

                            if (googleDriveFile.data) {
                                this.setState({
                                    pdfBlob: googleDriveFile.data,
                                    error: null,
                                })
                            } else {
                                throw new Error(
                                    'No PDF data received from Google Drive',
                                )
                            }
                        } else {
                            throw new Error('No file path found for invoice')
                        }
                    } catch (error) {
                        console.error(
                            'Error loading PDF from Google Drive:',
                            error,
                        )
                        this.setState({
                            error:
                                error instanceof Error
                                    ? `Failed to load PDF: ${error.message}`
                                    : 'Failed to load PDF',
                            pdfBlob: null,
                        })
                    }
                } else {
                    // Update state even if no invoice was found
                    this.setState({
                        emailData: this.state.emailData
                            ? {
                                  ...this.state.emailData,
                                  analysisStatus: 'full-success',
                              }
                            : null,
                    })
                }
            } catch (error) {
                // TODO: potentially do something with this error
            }
        })
    }

    confirmMatch = async (associationId: string): Promise<void> => {
        const cache = this.deps.services.cache
        const association = cache.getKey('documentTransactionAssociations')[
            associationId
        ]

        if (!association) {
            throw new Error('Association not found')
        }

        await this.deps.services.transactionInvoiceMatching.confirmAssociation(
            association.documentId,
            association.transactionId,
        )
    }

    removeMatch = async (associationId: string): Promise<void> => {
        const cache = this.deps.services.cache
        const association = cache.getKey('documentTransactionAssociations')[
            associationId
        ]

        if (!association) {
            throw new Error('Association not found')
        }

        await this.deps.services.transactionInvoiceMatching.removeAssociation(
            association.transactionId,
            association.documentId,
        )
    }

    updateMatchLoadingState(state: TaskState): void {
        this.setState({ matchLoadingState: state })
    }
}
